import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useState, type DragEvent } from 'react';
import type { ActivePermission } from 'src/pages/subscriptions/productPermissionModules';
import { LicenseCounter } from './LicenseCounter';
import { ItemModuleDetails } from './ModuleBreakdown';
import type { CatalogueItem, SubscriptionCycle, SubscriptionLineItem } from './types';
import { DEFAULT_LICENSES } from './types';

const DRAG_TYPE = 'application/x-subscription-catalogue-id';

const itemsForCycle = (items: CatalogueItem[], cycle: SubscriptionCycle) =>
  items.filter((item) => item.msrpKind === cycle);

interface CataloguePickerProps {
  catalogue: CatalogueItem[];
  loading?: boolean;
  selected: SubscriptionLineItem[];
  onChange: (items: SubscriptionLineItem[]) => void;
  activePermissions: ActivePermission[];
}

export const CataloguePicker = ({
  catalogue,
  loading = false,
  selected,
  onChange,
  activePermissions,
}: CataloguePickerProps) => {
  const theme = useTheme();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewCycle, setViewCycle] = useState<SubscriptionCycle>('monthly');
  const selectedIds = new Set(selected.map((item) => item.productId));
  const lockedCycle: SubscriptionCycle | null = (() => {
    const first = selected.find((item) => item.productId);
    if (!first) return null;
    return catalogue.find((option) => option.value === first.productId)?.msrpKind ?? null;
  })();

  const addItems = (productIds: string[]) => {
    const next = [...selected];
    const ids = new Set(next.map((item) => item.productId));
    const firstKind = productIds
      .map((productId) => catalogue.find((item) => item.value === productId)?.msrpKind)
      .find((kind): kind is SubscriptionCycle => kind === 'monthly' || kind === 'yearly');
    const activeCycle = lockedCycle || firstKind || null;

    productIds.forEach((productId) => {
      const option = catalogue.find((item) => item.value === productId);
      if (!option?.msrpKind || ids.has(productId)) return;
      if (activeCycle && option.msrpKind !== activeCycle) return;
      ids.add(productId);
      next.push({ productId, licenses: DEFAULT_LICENSES });
    });
    onChange(next);
  };

  const removeItems = (productIds: string[]) => {
    const remove = new Set(productIds);
    onChange(selected.filter((item) => !remove.has(item.productId)));
  };

  const addItem = (productId: string) => addItems([productId]);
  const removeItem = (productId: string) => removeItems([productId]);

  const toggleItem = (productId: string, checked: boolean) => {
    if (checked) addItem(productId);
    else removeItem(productId);
  };

  const toggleGroup = (items: CatalogueItem[], checked: boolean) => {
    const ids = items.map((item) => item.value);
    if (checked) addItems(ids);
    else removeItems(ids);
  };

  const setLicenses = (productId: string, licenses: number) => {
    onChange(
      selected.map((item) =>
        item.productId === productId ? { ...item, licenses } : item,
      ),
    );
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const productId =
      event.dataTransfer.getData(DRAG_TYPE) || event.dataTransfer.getData('text/plain');
    if (productId) addItem(productId);
  };

  useEffect(() => {
    if (expandedId && !selected.some((item) => item.productId === expandedId)) {
      setExpandedId(null);
    }
  }, [expandedId, selected]);

  useEffect(() => {
    if (lockedCycle) setViewCycle(lockedCycle);
  }, [lockedCycle]);

  const toggleDetails = (productId: string) => {
    setExpandedId((current) => (current === productId ? null : productId));
  };

  const cycleItems = itemsForCycle(catalogue, viewCycle);
  // Hide already-selected catalogue items; remove from the selected list to pick again.
  const available = cycleItems.filter((item) => !selectedIds.has(item.value));
  const products = available.filter((item) => item.entityType === 'PRODUCT');
  const packages = available.filter((item) => item.entityType === 'PACKAGE');
  const cycleEmpty = products.length === 0 && packages.length === 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" paddingY={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns="minmax(0, 1fr) 280px"
      style={{ gap: 16, alignItems: 'stretch' }}>
      <Box display="flex" flexDirection="column" style={{ gap: 12, minWidth: 0 }}>
        <Typography style={{ fontSize: 13, color: theme.palette.text.secondary }}>
          Please select one or more products or packages to complete the subscription.
        </Typography>
        <Box
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          style={{
            minHeight: 180,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 8,
            padding: 16,
            backgroundColor: theme.palette.grey[50],
          }}>
          {selected.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ minHeight: 148 }}>
              <Typography style={{ fontSize: 14, color: theme.palette.text.disabled }}>
                Drag and Drop one or more Products or Packages here
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
              {selected.map((item) => {
                const option = catalogue.find((entry) => entry.value === item.productId);
                const isExpanded = expandedId === item.productId;
                return (
                  <Box
                    key={item.productId}
                    style={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      padding: '8px 12px',
                    }}>
                    <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                      <Box style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
                        <Typography
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {option?.label || item.productId}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: 11,
                            color: theme.palette.text.secondary,
                            textTransform: 'capitalize',
                          }}>
                          {option?.entityType === 'PACKAGE' ? 'Package' : 'Product'}
                          {option?.msrpKind ? ` · ${option.msrpKind}` : ''}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        style={{ gap: 4, flexShrink: 0 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <LicenseCounter
                            value={item.licenses}
                            onChange={(licenses) => setLicenses(item.productId, licenses)}
                          />
                          <Tooltip title="Remove product" placement="top" arrow>
                            <IconButton
                              size="small"
                              onClick={() => removeItem(item.productId)}
                              style={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 6,
                              }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {option ? (
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => toggleDetails(item.productId)}
                            endIcon={
                              <KeyboardArrowDownIcon
                                style={{
                                  fontSize: 18,
                                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.15s ease',
                                }}
                              />
                            }
                            sx={{
                              minWidth: 0,
                              px: 0.5,
                              py: 0.75,
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'none',
                              color: theme.palette.primary.main,
                            }}>
                            {isExpanded ? 'Hide details' : 'Show details'}
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                    {isExpanded && option ? (
                      <ItemModuleDetails
                        item={option}
                        activePermissions={activePermissions}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        style={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          padding: 12,
          backgroundColor: theme.palette.background.paper,
          maxHeight: 420,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}>
        <Box
          display="flex"
          style={{
            gap: 2,
            marginBottom: 12,
            flexShrink: 0,
            padding: 3,
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}>
          {(['monthly', 'yearly'] as const).map((cycle) => {
            const active = viewCycle === cycle;
            const highlight = alpha(theme.palette.primary.main, 0.1);
            return (
              <Button
                key={cycle}
                type="button"
                variant="text"
                disableRipple
                disabled={Boolean(lockedCycle)}
                onClick={() => setViewCycle(cycle)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  py: 0.6,
                  px: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  lineHeight: 1.3,
                  color: active
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  backgroundColor: active ? highlight : 'transparent',
                  border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.28) : 'transparent'
                    }`,
                  transition: 'none',
                  '&:hover': {
                    backgroundColor: active ? highlight : 'transparent',
                  },
                  '&.Mui-disabled': {
                    color: active
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    backgroundColor: active ? highlight : 'transparent',
                    border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.28) : 'transparent'
                      }`,
                    opacity: 0.7,
                  },
                }}>
                {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
              </Button>
            );
          })}
        </Box>
        <Box style={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
          {catalogue.length === 0 || cycleEmpty ? (
            <Typography style={{ fontSize: 13, color: theme.palette.text.secondary }}>
              No {viewCycle} products or packages available
            </Typography>
          ) : (
            <>
              <CatalogueGroup
                title="Products"
                items={products}
                selectedIds={selectedIds}
                onToggle={toggleItem}
                onToggleAll={(checked) => toggleGroup(products, checked)}
              />
              <CatalogueGroup
                title="Packages"
                items={packages}
                selectedIds={selectedIds}
                onToggle={toggleItem}
                onToggleAll={(checked) => toggleGroup(packages, checked)}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CatalogueGroup = ({
  title,
  items,
  selectedIds,
  onToggle,
  onToggleAll,
}: {
  title: string;
  items: CatalogueItem[];
  selectedIds: Set<string>;
  onToggle: (productId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}) => {
  const theme = useTheme();
  if (items.length === 0) return null;

  const selectedCount = items.filter((item) => selectedIds.has(item.value)).length;
  const allSelected = selectedCount === items.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <Box style={{ marginBottom: 10 }}>
      <Box display="flex" alignItems="center" style={{ gap: 4, paddingBottom: 8 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(event) => onToggleAll(event.target.checked)}
        />
        <Typography style={{ fontSize: 12, fontWeight: 700, color: theme.palette.text.secondary }}>
          {title}
        </Typography>
      </Box>
      {items.map((item) => {
        const checked = selectedIds.has(item.value);
        return (
          <Box
            key={item.value}
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData(DRAG_TYPE, item.value);
              event.dataTransfer.setData('text/plain', item.value);
              event.dataTransfer.effectAllowed = 'copy';
            }}
            display="flex"
            alignItems="center"
            style={{
              gap: 4,
              cursor: 'grab',
              marginBottom: 8,
              padding: '2px 10px 2px 4px',
              borderRadius: 999,
              backgroundColor: alpha(theme.palette.primary.main, checked ? 0.1 : 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, checked ? 0.28 : 0.16)}`,
              color: theme.palette.primary.main,
            }}>
            <Checkbox
              size="small"
              checked={checked}
              onMouseDown={(event) => event.stopPropagation()}
              onChange={(event) => onToggle(item.value, event.target.checked)}
              sx={{
                color: theme.palette.primary.main,
                '&.Mui-checked': { color: theme.palette.primary.main },
                p: 0.5,
              }}
            />
            <Typography
              title={item.label}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.primary.main,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
